import {
  goalDetailSchema,
  goalListSchema,
  type AddKeyResultRequest,
  type ArchiveGoalRequest,
  type ChangeGoalStatusRequest,
  type CreateGoalRequest,
  type GoalHorizon,
  type GoalLinkRequest,
  type UpdateGoalRequest,
  type UpdateKeyResultRequest,
} from '@mfos/shared';
import { useQuery } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut, withQuery } from '../../lib/api-client';
import { workspaceKey, workspacePath } from '../workspace/workspace.api';
import { useWorkspaceMutation } from '../workspace/workspace-mutation';

export interface GoalFilters {
  /** Comma-separated statuses; empty = everything except cancelled. */
  readonly status?: string;
  readonly horizon?: GoalHorizon;
  readonly includeArchived?: boolean;
}

export const goalsKey = (workspaceId: string) => [...workspaceKey(workspaceId), 'goals'] as const;
export const goalKey = (workspaceId: string, goalId: string) =>
  [...goalsKey(workspaceId), 'detail', goalId] as const;

export function useGoals(workspaceId: string, filters: GoalFilters = {}, enabled = true) {
  return useQuery({
    queryKey: [...goalsKey(workspaceId), 'list', filters] as const,
    queryFn: ({ signal }) =>
      apiGet(
        withQuery(workspacePath(workspaceId, 'goals'), {
          status: filters.status,
          horizon: filters.horizon,
          includeArchived: filters.includeArchived === true ? 'true' : undefined,
        }),
        { schema: goalListSchema, signal },
      ),
    enabled,
  });
}

export function useGoal(workspaceId: string, goalId: string, enabled = true) {
  return useQuery({
    queryKey: goalKey(workspaceId, goalId),
    queryFn: ({ signal }) =>
      apiGet(workspacePath(workspaceId, 'goals', goalId), { schema: goalDetailSchema, signal }),
    enabled,
  });
}

export function useCreateGoal(workspaceId: string) {
  return useWorkspaceMutation(workspaceId, (body: CreateGoalRequest) =>
    apiPost(workspacePath(workspaceId, 'goals'), body, goalDetailSchema),
  );
}

/** Every goal mutation answers with the refreshed goal detail. */
export function useGoalMutations(workspaceId: string, goalId: string) {
  const path = (...segments: string[]) => workspacePath(workspaceId, 'goals', goalId, ...segments);
  return {
    update: useWorkspaceMutation(workspaceId, (body: UpdateGoalRequest) =>
      apiPut(path(), body, goalDetailSchema),
    ),
    changeStatus: useWorkspaceMutation(workspaceId, (body: ChangeGoalStatusRequest) =>
      apiPost(path('status'), body, goalDetailSchema),
    ),
    archive: useWorkspaceMutation(workspaceId, (body: ArchiveGoalRequest) =>
      apiPost(path('archive'), body, goalDetailSchema),
    ),
    addKeyResult: useWorkspaceMutation(workspaceId, (body: AddKeyResultRequest) =>
      apiPost(path('key-results'), body, goalDetailSchema),
    ),
    updateKeyResult: useWorkspaceMutation(
      workspaceId,
      ({ keyResultId, body }: { keyResultId: string; body: UpdateKeyResultRequest }) =>
        apiPut(path('key-results', keyResultId), body, goalDetailSchema),
    ),
    archiveKeyResult: useWorkspaceMutation(
      workspaceId,
      ({ keyResultId, version }: { keyResultId: string; version: number }) =>
        apiPost(path('key-results', keyResultId, 'archive'), { version }, goalDetailSchema),
    ),
    link: useWorkspaceMutation(workspaceId, (body: GoalLinkRequest) =>
      apiPost(path('links'), body, goalDetailSchema),
    ),
    unlink: useWorkspaceMutation(workspaceId, (body: GoalLinkRequest) =>
      apiPost(path('links', 'remove'), body, goalDetailSchema),
    ),
  };
}

export type GoalMutations = ReturnType<typeof useGoalMutations>;
